import type { ReactNode } from "react";

export type PhoneSize = "sm" | "md" | "lg" | "xl";

type IPhoneMockupProps = {
  /** Content to display inside the phone screen */
  children?: ReactNode;
  /** Additional CSS classes for the outer container */
  className?: string;
  /** Whether to show the shadow effect */
  showShadow?: boolean;
  /** Phone size variant */
  size?: PhoneSize;
};

const sizeConfig = {
  sm: {
    frame: "h-[400px] w-[200px] rounded-[1.75rem] border-[10px]",
    notch: "h-[12px] w-[100px]",
    volumeTop: "top-[84px] h-[32px] -start-[13px]",
    volumeBottom: "top-[120px] h-[32px] -start-[13px]",
    power: "top-[96px] h-[44px] -end-[13px]",
    screen: "rounded-[1rem]",
  },
  md: {
    frame: "h-[540px] w-[270px] rounded-[2.25rem] border-[12px]",
    notch: "h-[16px] w-[135px]",
    volumeTop: "top-[112px] h-[42px] -start-[15px]",
    volumeBottom: "top-[162px] h-[42px] -start-[15px]",
    power: "top-[128px] h-[58px] -end-[15px]",
    screen: "rounded-[1.25rem]",
  },
  lg: {
    frame: "h-[660px] w-[330px] rounded-[2.75rem] border-[15px]",
    notch: "h-[20px] w-[163px]",
    volumeTop: "top-[136px] h-[51px] -start-[18px]",
    volumeBottom: "top-[196px] h-[51px] -start-[18px]",
    power: "top-[156px] h-[70px] -end-[18px]",
    screen: "rounded-[1.75rem]",
  },
  xl: {
    frame: "h-[780px] w-[390px] rounded-[3rem] border-[18px]",
    notch: "h-[24px] w-[192px]",
    volumeTop: "top-[161px] h-[60px] -start-[21px]",
    volumeBottom: "top-[231px] h-[60px] -start-[21px]",
    power: "top-[185px] h-[83px] -end-[21px]",
    screen: "rounded-[2rem]",
  },
};

/**
 * iPhone 12 mockup component with iOS-style notch
 * Displays content in a realistic iPhone frame
 */
export function IPhoneMockup({
  children,
  className = "",
  showShadow = true,
  size = "lg",
}: IPhoneMockupProps) {
  const config = sizeConfig[size];

  return (
    <div
      className={`relative mx-auto ${config.frame} border-gray-800 bg-gray-800 dark:border-gray-800 dark:bg-gray-800 ${showShadow ? "shadow-xl" : ""} ${className}`}
    >
      {/* Notch */}
      <div
        className={`-translate-x-1/2 absolute top-0 left-1/2 ${config.notch} rounded-b-2xl bg-gray-800`}
      />

      {/* Volume buttons */}
      <div
        className={`absolute ${config.volumeTop} w-0.75 rounded-s-lg bg-gray-800`}
      />
      <div
        className={`absolute ${config.volumeBottom} w-0.75 rounded-s-lg bg-gray-800`}
      />

      {/* Power button */}
      <div
        className={`absolute ${config.power} w-0.75 rounded-e-lg bg-gray-800`}
      />

      {/* Screen */}
      <div
        className={`h-full w-full ${config.screen} overflow-hidden bg-white dark:bg-black`}
      >
        {children}
      </div>
    </div>
  );
}
